import { useEffect, useState } from "react";
import {
    Droplets,
    Disc3,
    Wrench,
    ClipboardCheck,
    Gauge,
    BatteryCharging,
} from "lucide-react";

const ICONS = {
    "01": Droplets,
    "02": Disc3,
    "03": Wrench,
    "04": ClipboardCheck,
    "05": Gauge,
    "06": BatteryCharging,
};

const FALLBACK = [
    { code: "01", name: "Oil & Filter Change", duration: "30-45 min", starts_at: 79, blurb: "Full synthetic or conventional. We bring the oil, filter and dispose of the old." },
    { code: "02", name: "Brake Service", duration: "60-90 min", starts_at: 159, blurb: "Pads, rotors, calipers and fluid. Inspection first — we never replace what isn't worn." },
    { code: "03", name: "Tune-Up", duration: "60-90 min", starts_at: 139, blurb: "Plugs, filters, fluids and a multi-point check to keep your engine running like new." },
    { code: "04", name: "Pre-Purchase Inspection", duration: "60-75 min", starts_at: 99, blurb: "Buying used? We meet you at the seller and give you a no-BS verdict before you sign." },
    { code: "05", name: "Diagnostics / Check Engine", duration: "30-60 min", starts_at: 89, blurb: "OBD-II scan with manufacturer-grade tools. Plain-English explanation, every time." },
    { code: "06", name: "Battery & Electrical", duration: "20-40 min", starts_at: 129, blurb: "Battery test, replacement, alternator and starter checks — wherever you're stuck." },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Services() {
    const [services, setServices] = useState(FALLBACK);

    useEffect(() => {
        fetch(`${API}/services`)
            .then((r) => r.json())
            .then((d) => {
                if (d?.services?.length) setServices(d.services);
            })
            .catch(() => {});
    }, []);

    return (
        <section
            id="services"
            data-testid="services-section"
            className="border-b hairline"
        >
            <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                <div className="col-span-12 md:col-span-4 px-6 md:px-12 py-14 md:py-20 border-r hairline">
                    <div className="font-mono-tech text-[11px] text-[#1d4ed8] mb-6">
                        SERVICES
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl leading-[0.95] tracking-tighter">
                        Everything <br />
                        a shop can do.
                        <br />
                        <span className="text-[#1d4ed8]">
                            In your driveway.
                        </span>
                    </h2>
                    <p className="text-[#52525b] mt-6 leading-relaxed">
                        Quiseo Auto Repair brings dealership-grade tools and
                        diagnostic equipment to you. Flat-rate pricing, OEM
                        parts available, and a 12-month / 12,000-mile warranty
                        on every repair.
                    </p>
                    <div className="mt-8 font-mono-tech text-[10px] text-[#52525b]">
                        Don&apos;t see what you need? Call us — if a shop can fix
                        it, chances are we can fix it on-site.
                    </div>
                </div>

                <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {services.map((s) => {
                        const Icon = ICONS[s.code] || Wrench;
                        return (
                            <article
                                key={s.code}
                                data-testid={`service-card-${s.code}`}
                                className="border-l border-b hairline p-8 group hover:bg-[#0a0a0a] hover:text-white transition-colors duration-150 cursor-default flex flex-col"
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <div className="font-mono-tech text-[11px] opacity-60">
                                        {s.code}
                                    </div>
                                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-display text-xl leading-tight mb-3">
                                    {s.name}
                                </h3>
                                {s.blurb && (
                                    <p className="text-[13px] leading-relaxed opacity-80 flex-1">
                                        {s.blurb}
                                    </p>
                                )}
                                <div className="mt-6 pt-5 border-t hairline group-hover:border-white/20 flex items-center justify-between">
                                    <span className="font-mono-tech text-[10px] opacity-70">
                                        {s.duration}
                                    </span>
                                    <span className="font-mono-tech text-[11px]">
                                        from ${s.starts_at}
                                    </span>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
